// Reshapes a one-groupby, one-metric ("pivot") ChartResult into buckets +
// ChartSeries for BarChart/LineAreaChart. chart-result-adapter.ts's
// `seriesFromResult` throws `UnsupportedChartShape` on any groupby; this is the
// handler for the one pivot shape the real corpus uses (9 live charts, all one
// groupby + one metric). Other pivot shapes still throw.
//
// Column order is chart-result-adapter.ts's contract: the groupby column at row
// index 0, then the bucket column (read via `bucketsFromResult`), then the
// metric at `resolveDimensionCount(chart, result)`.
import type { ChartSeries } from "@bi/shared";
import type { ChartQuery, ChartResult, ChartSpec } from "@bi/shared/types";

import {
  UnsupportedChartShape,
  bucketsFromResult,
  metricRefs,
  resolveDimensionCount,
} from "./chart-result-adapter";

/** chart-color.ts's `CHART_CATEGORICAL_SLOTS` — bar/line are ADJACENT chart
 *  forms (any two series need not sit side by side the way donut wedges
 *  do), so unlike `foldPieSlicesToThree`'s 3-slot all-pairs cap, a pivot may
 *  use every validated categorical slot before folding to "Other". */
const MAX_REAL_SERIES = 8;

/** The most series a SEQUENTIAL ramp will draw before this falls back to the
 *  categorical fold. Not a palette limit - a ramp has as many steps as it needs
 *  - but a legibility one: past roughly two months of daily cohorts the lines
 *  stop being individually traceable no matter how they are colored, and a
 *  chart that silently renders 300 of them is worse than one that says it
 *  folded. 62 is chosen so a two-month daily cohort chart fits whole. */
const MAX_ORDINAL_SERIES = 62;

/** The calendar-date label forms that make a series set ORDINAL. A closed list
 *  of DATE forms on purpose: a numeric id sorts too, but drawn as a ramp it would
 *  claim id 4 and id 5 are neighbors. Widen it only for another label kind whose
 *  adjacency is real.
 *
 *  `MM-DD` is year-less, so ordering is lexical on the original label. A window
 *  that crosses a new year orders 12-31 before 01-01; such a chart needs
 *  `YYYY-MM-DD` labels from its dataset. */
const ORDINAL_LABEL_FORMS: readonly RegExp[] = [
  /^\d{4}-\d{2}-\d{2}$/, // 2026-08-03
  /^\d{4}-\d{2}$/, // 2026-08
  /^\d{2}-\d{2}$/, // 08-03
];

/** True when every label shares ONE date form and there are at least two of
 *  them. Requiring a single form is not pedantry: a set mixing `08-03` with
 *  `2026-08-03` has no defensible order (the short one has no year to compare),
 *  and treating it as ordinal would put the ramp in an arbitrary sequence while
 *  looking authoritative about it. A mixed set falls back to categorical, where
 *  an arbitrary-looking order is at least honest. */
export function isOrdinalSeriesSet(labels: readonly string[]): boolean {
  if (labels.length < 2 || labels.length > MAX_ORDINAL_SERIES) return false;
  return ORDINAL_LABEL_FORMS.some((form) => labels.every((l) => form.test(l)));
}

/** Key for the synthetic fold-in series, mirroring `donut-fold.ts`'s
 *  `DONUT_OTHER_KEY` pattern (a dedicated sentinel a caller can recognize
 *  without string-matching a label a locale might translate). */
export const PIVOT_OTHER_KEY = "__pivot_other__";

/** Fallback for a result cached before `ChartResult.applied_row_limit` existed
 *  (3-hour TTL), when the server's bare default was 500. `compiler.py` now
 *  defaults to `MAX_ROW_LIMIT`. Delete with `believedServerRowLimit` once no
 *  such cached result can remain. */
const DEFAULT_SERVER_ROW_LIMIT = 500;

/** Client-side guess at the applied limit, used only by `isPossiblyTruncated`
 *  for a result cached before `applied_row_limit` existed. Known gap: it cannot
 *  see `MAX_ROW_LIMIT`, so a chart authoring a `row_limit` above it reads a
 *  truncated result as complete (a false negative). */
function believedServerRowLimit(query: ChartQuery): number {
  return query.row_limit ?? DEFAULT_SERVER_ROW_LIMIT;
}

export interface PivotResult {
  /** Ascending, deduplicated x-axis labels — see the module-level comment on
   *  `sortBucketsStably` for why this is a plain string sort, not a date
   *  parse. */
  buckets: string[];
  /** 1..8 real series, SELECTED by total value descending (the reader's
   *  actual question - "who spent/ran the most") but RENDERED in
   *  alphabetical order within that kept set - see `pivotFromResult`'s own
   *  header comment for why selection and render/color order are two
   *  separate decisions here. Plus, when the result has more than 8
   *  distinct series, one trailing synthetic `PIVOT_OTHER_KEY` series
   *  summing everything past the cap. */
  series: ChartSeries[];
  /** Distinct series in this result BEFORE folding — for a caller's "top N
   *  of M" note. */
  totalSeriesCount: number;
  /** How many of `totalSeriesCount` are rendered as their own real series.
   *  Equals `totalSeriesCount` when nothing was folded. */
  keptSeriesCount: number;
  /** Which color scale the caller must use for `series`, and the reason the
   *  fold above may not have run at all.
   *
   *  `"categorical"` - the series are unrelated identities; hand the keys to
   *  `useCategoricalColors`. At most 8 of them (7 + "Other") ever arrive.
   *
   *  `"sequential"` - the series are a CALENDAR-DATE sequence (see
   *  `isOrdinalSeriesSet`) rendered in date order, uncapped up to
   *  `MAX_ORDINAL_SERIES`, with NO "Other". Hand the keys to
   *  `useSequentialColors`: position on the ramp is the encoding, so coloring
   *  these from the 8-slot categorical palette would overflow every series past
   *  the 8th onto the same neutral token and destroy the only thing the chart
   *  says. */
  seriesScale: "categorical" | "sequential";
  /** True once `row_count` meets the applied cap (see `isPossiblyTruncated`).
   *  `series`/`buckets` are still computed, but a caller MUST NOT render them:
   *  in a truncated result the zero-fill cannot tell a real `(series, bucket)`
   *  absence from a pair the `LIMIT` dropped, so a bar could show a false zero.
   *  `chart-card.tsx`'s `PivotTruncatedMessage` withholds the chart instead
   *  (unlike `ignoresDateRange`/`authoredConstraints`, where the number stays
   *  correct and renders with a note). */
  possiblyTruncated: boolean;
}

function cellToString(cell: unknown): string {
  return cell === null || cell === undefined ? "Unknown" : String(cell);
}

function toNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Best-effort read of whether `result` may be a truncated sample: `row_count`
 *  against the applied limit. One-directional: `>=` reads as "possibly
 *  truncated", because a result with exactly that many rows looks identical on
 *  the wire. A false positive is the safe direction; a false negative is not. */
export function isPossiblyTruncated(query: ChartQuery, result: ChartResult): boolean {
  // Prefer the server's applied limit. The client guess covers only a result
  // cached before that field existed (3-hour TTL). Do not hard-code 500 here:
  // the server default was raised, so 500 would mark complete results truncated.
  const limit = result.applied_row_limit ?? believedServerRowLimit(query);
  return result.row_count >= limit;
}

/** Plain ascending string sort, deliberately NOT a date parse. The compiler
 *  emits no ORDER BY for this shape, so rows arrive in a non-reproducible order
 *  and some sort is mandatory. ISO `YYYY-MM-DD` buckets already sort
 *  chronologically as strings, and a non-date bucket (one real chart uses
 *  `x_axis: "kpi"`) still gets the same order every refresh. */
function sortBucketsStably(buckets: Iterable<string>): string[] {
  return [...buckets].sort();
}

/** Reshapes a one-groupby, one-metric `ChartResult` into `{ buckets, series }`
 *  for `BarChart`/`LineAreaChart`. Throws `UnsupportedChartShape` for any other
 *  pivot shape.
 *
 *  Categorical series: selection and render order are separate decisions.
 *  - Selection (who survives the fold) is BY TOTAL VALUE, descending. An
 *    alphabetical selection kept a $0.00 agent (rank 34 of 35) and folded the
 *    top spenders into "Other" on "LLM cost per day by agent".
 *  - Render/color order is ALPHABETICAL within the kept set, computed after
 *    selection. `assignCategoricalColors` assigns slots by array position and
 *    forbids a value-derived order, which would reshuffle colors every refresh.
 *    Color then moves only when the kept set's membership changes.
 *    `PivotResult.series` is already in render order.
 *  - Folding follows `donut-fold.ts`'s structure at `MAX_REAL_SERIES`. "Other"
 *    is always last. A result with <= 8 series folds nothing, including the
 *    single-series case.
 *
 *  Ordinal series (`isOrdinalSeriesSet`, calendar-date cohorts) skip selection,
 *  the cap and the fold: every series is kept in date order and `seriesScale`
 *  is `"sequential"`. A fold answers "which day earned most", which a maturation
 *  chart does not ask. See `sequentialRampVar` in shared's chart-color.ts. */
export function pivotFromResult(chart: ChartSpec, result: ChartResult): PivotResult {
  const groupby = chart.query.groupby ?? [];
  const metrics = metricRefs(chart.query);
  if (groupby.length !== 1 || metrics.length !== 1) {
    throw new UnsupportedChartShape(
      `${chart.slice_name}: a pivot needs exactly one groupby column and one metric ` +
        `(has ${groupby.length} groupby column(s), ${metrics.length} metric(s)) - this shape isn't handled yet`,
    );
  }

  const dimCount = resolveDimensionCount(chart, result);
  const metricIndex = dimCount; // one metric, always the column right after the dims
  const rowBuckets = bucketsFromResult(chart, result); // per-row, aligned to result.rows

  const bucketSet = new Set<string>();
  // Raw label -> bucket -> value. A Map keyed by the raw string IS the
  // identity here: unlike a declared MetricRef (a free-text `label` an
  // author can edit independently of what data feeds it), a pivot's
  // groupby value has no separate id on the wire at all - the string BigQuery
  // returns for a group is the only handle this module ever has on it, and
  // it is what `ChartSeries.key` becomes below.
  const perSeries = new Map<string, Map<string, number>>();
  const seriesOrder: string[] = [];

  result.rows.forEach((row, i) => {
    const label = cellToString(row[0]);
    const bucket = rowBuckets[i] ?? "";
    bucketSet.add(bucket);
    let byBucket = perSeries.get(label);
    if (!byBucket) {
      byBucket = new Map();
      perSeries.set(label, byBucket);
      seriesOrder.push(label);
    }
    // Last value wins for a repeated (label, bucket) pair. A complete result
    // never repeats one (the query groups by both), so this only matters for
    // a truncated sample that happened to return the same pair twice - and
    // there "last" is as good an answer as any other arbitrary pick.
    byBucket.set(bucket, toNumber(row[metricIndex]));
  });

  const buckets = sortBucketsStably(bucketSet);

  const totalFor = (label: string): number => {
    let sum = 0;
    for (const v of perSeries.get(label)?.values() ?? []) sum += v;
    return sum;
  };
  // Selection: total value descending. Ties (including the common
  // "every series has the same total" case some tests exercise) break
  // alphabetically ascending, purely for a deterministic result - it is
  // NOT the render order, see below.
  const rankedByValue = [...seriesOrder].sort((a, b) => {
    const diff = totalFor(b) - totalFor(a);
    if (diff !== 0) return diff;
    return a < b ? -1 : a > b ? 1 : 0;
  });

  // ORDINAL SHORT-CIRCUIT: a calendar-date set keeps every series, in date order
  // (see the header). Lexical order on the label is chronological for every
  // form `isOrdinalSeriesSet` admits.
  const ordinal = isOrdinalSeriesSet(seriesOrder);
  const cap = ordinal
    ? seriesOrder.length
    : // Spends slot 8 on the synthetic "Other" once folding is needed: 7 REAL
      // series + Other, never 8 real + a 9th "Other" - there is no 9th
      // categorical slot to spend on it (categoricalSlotVar overflows index 8+
      // onto the same neutral --rc-chart-other token `Other` already uses, so
      // an unfolded 8th-plus-Other would silently share a color with "Other"
      // anyway). Do not "fix" this to `MAX_REAL_SERIES` - that would draw 9
      // series through an 8-slot palette.
      rankedByValue.length > MAX_REAL_SERIES
      ? MAX_REAL_SERIES - 1
      : MAX_REAL_SERIES;
  const keptByValue = rankedByValue.slice(0, cap);
  const folded = rankedByValue.slice(cap);

  // Render/color order. Categorical: alphabetical WITHIN the kept set, computed
  // only now that selection has already decided WHO is kept - see this
  // function's own header comment for why those are two separate steps.
  // Ordinal: date order, which is the ENCODING rather than a stability
  // convention, so it is not negotiable the way the alphabetical case is.
  const keptInRenderOrder = [...keptByValue].sort();

  const series: ChartSeries[] = keptInRenderOrder.map((label) => {
    const byBucket = perSeries.get(label);
    return {
      key: label,
      label,
      values: buckets.map((b) => byBucket?.get(b) ?? 0),
    };
  });

  if (folded.length > 0) {
    const values = buckets.map((b) =>
      folded.reduce((sum, label) => sum + (perSeries.get(label)?.get(b) ?? 0), 0),
    );
    series.push({ key: PIVOT_OTHER_KEY, label: "Other", values });
  }

  return {
    buckets,
    series,
    totalSeriesCount: rankedByValue.length,
    keptSeriesCount: keptByValue.length,
    seriesScale: ordinal ? "sequential" : "categorical",
    possiblyTruncated: isPossiblyTruncated(chart.query, result),
  };
}
